import { Field, useFormikContext } from 'formik';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { HTMLAttributes, useEffect, useMemo } from 'react';
import AddressSearch from 'react-loqate';
import { Blok } from '../../../interfaces/Blok.interface';
import FormField from '../../FormFields/FormField/FormField';
import { FormSectionBlokProps } from '../FormSectionBlok.interface';
import classNames from 'classnames';

interface FormSectionAddressBlokProps extends FormSectionBlokProps {
  key: string;
}

interface StaticFormSectionProps {
  blok: FormSectionAddressBlokProps;
}

interface Address {
  street: string;
  addressLine2: string;
  city: string;
  postcode: string;
}

const StaticFormSectionAddress: React.FC<StaticFormSectionProps> = ({
  blok,
}) => {
  const { setFieldValue, errors, values } = useFormikContext();
  const { heading, description, form_fields, sectionNumber, key } = blok;
  const [address, setAddress] = React.useState<Address | null>(null);
  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [isManualFormDisplayed, setIsManualFormDisplayed] =
    React.useState<boolean>(false);

  const [isReady, setIsReady] = React.useState<boolean>(false);

  const AddressSearchInput = useMemo(() => {
    return (props: HTMLAttributes<HTMLInputElement>) => (
      <Field
        type="text"
        name={blok._uid}
        autoComplete="chrome-off"
        {...props}
      />
    );
  }, [blok._uid]);

  const hasErrors = (errors: any) => {
    // Define the suffixes we are interested in.
    const suffixes = ['-city', '-postcode', '-street', '-addressLine2'];

    // Retrieve all the keys from the object.
    const keys = Object.keys(errors);

    // Check if any key ends with one of the specified suffixes.
    return keys.some((key) => suffixes.some((suffix) => key.endsWith(suffix)));
  };

  const handleAddressSelect = (address: any) => {
    const parts = [
      address.BuildingName,
      address.BuildingNumber,
      address.Street,
    ].filter(Boolean);

    const street = parts.join(' ');

    setAddress({
      street,
      addressLine2: address.Line2,
      city: address.City,
      postcode: address.PostalCode,
    });

    setFieldValue(`${key}-street`, street);
    setFieldValue(`${key}-addressLine2`, address.Line2);
    setFieldValue(`${key}-city`, address.City);
    setFieldValue(`${key}-postcode`, address.PostalCode);

    setIsManualFormDisplayed(true);
    setShowErrors(false);
  };

  useEffect(() => {
    // Refactor to use key as well
    if (hasErrors(errors)) {
      setShowErrors(true);
    } else {
      setShowErrors(false);
    }
  }, [hasErrors(errors), values]);

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) return null;

  return (
    <div {...storyblokEditable(blok)}>
      <section
        className={`mx-3.5 pb-6 border-b border-b-border/default sm:mx-0 ${
          sectionNumber && 'pt-12'
        }`}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-start text-2xl gap-x-4">
            {sectionNumber && (
              <div className="w-9 h-9 rounded-full bg-icon/icon-active-bg flex items-center justify-center">
                <p className="text-system/white font-dm-serif-display">
                  {sectionNumber}
                </p>
              </div>
            )}
            <div className="text-text/body">
              <p className="font-dm-serif-display">{heading}</p>
              {description && <p className="text-sm">{description}</p>}
            </div>
          </div>
          <div className="space-y-12">
            {!isManualFormDisplayed && (
              <FormField label={'Find your address'} toolTip={[]} key={key}>
                <>
                  {/* className="h-14 flex bg-system/white border border-border/default rounded-default gap-x-3 overflow-clip" */}
                  <div
                    className={classNames(
                      'h-14 flex items-center justify-start bg-system/white border border-border/default rounded-default overflow-clip outline-none',
                      {
                        'border-border/error': showErrors,
                      }
                    )}
                  >
                    <AddressSearch
                      locale="en-GB"
                      inline={false}
                      apiKey="MA13-CB34-NW33-MT78"
                      countries={['GB']}
                      debounce={100}
                      components={{
                        Input: AddressSearchInput,
                      }}
                      className="address-search-box border-hidden w-full"
                      classes={{
                        input:
                          'flex-1 border-none h-full w-full focus:ring-0 focus:outline-none',
                        listItem: 'bg-[#fff] p-3 cursor-pointer',
                        list: 'bg=[#fff] overflow-y-scroll',
                      }}
                      onSelect={(address) => {
                        handleAddressSelect(address);
                      }}
                    />
                  </div>

                  <button
                    className="text/body-primary text-sm"
                    onClick={() => setIsManualFormDisplayed(true)}
                  >
                    Enter address manually
                  </button>
                </>
              </FormField>
            )}

            {isManualFormDisplayed && (
              <div className="space-y-12">
                {form_fields.map((formField: Blok) => (
                  <StoryblokComponent
                    blok={{ ...formField }}
                    key={formField._uid}
                  />
                ))}
              </div>
            )}
          </div>

          {showErrors && !isManualFormDisplayed && (
            <div className="text-text/body-primary">
              Please enter a valid address
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default StaticFormSectionAddress;
